import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    Grid,
    Alert,
    Button,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper
} from '@mui/material';
import {
    Pending,
    CheckCircle,
    Cancel,
    Visibility,
    Schedule,
    TrendingUp,
    Close,
    Refresh
} from '@mui/icons-material';
import React, { useState } from 'react';

const PlanUpgradeRequests = ({ upgradeRequests, onRefresh }) => {
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

    const getStatusColor = (status) => {
        switch (status) {
            case 'PENDING': return 'warning';
            case 'ACTIVE': return 'success';
            case 'REJECTED': return 'error';
            default: return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'PENDING': return <Pending />;
            case 'ACTIVE': return <CheckCircle />;
            case 'REJECTED': return <Cancel />;
            default: return <Schedule />;
        }
    };

    const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });

    const formatPrice = (plan) => {
        if (plan.monthly_price) {
            return `$${plan.monthly_price}/month`;
        }
        if (plan.one_time_price) {
            return `$${plan.one_time_price} one-time`;
        }
        return 'Free';
    };

    const handleViewDetails = (request) => {
        setSelectedRequest(request);
        setDetailsDialogOpen(true);
    };

    const handleCloseDetails = () => {
        setDetailsDialogOpen(false);
        setSelectedRequest(null);
    };

    const pendingRequests = upgradeRequests.filter(req => req.status === 'PENDING');
    const processedRequests = upgradeRequests.filter(req => req.status === 'ACTIVE');

    if (!upgradeRequests || upgradeRequests.length===0) {
        return (
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <Typography>No upgrade requests</Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Box>
            {/* Pending Requests Alert */}
            {pendingRequests.length > 0 && (
                <Alert
                    severity="info"
                    sx={{ mb: 3 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={onRefresh}
                            startIcon={<Refresh />}
                        >
                            Refresh
                        </Button>
                    }
                >
                    <Typography variant="subtitle2">
                        You have {pendingRequests.length} pending upgrade request{pendingRequests.length > 1 ? 's' : ''} waiting for admin approval.
                    </Typography>
                </Alert>
            )}

            {upgradeRequests.length === 0 ? (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <TrendingUp sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            No Upgrade Requests
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            You haven't submitted any plan upgrade requests yet.
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Grid container spacing={3}>
                    {upgradeRequests.map((request) => (
                        <Grid item size={12} key={request.id}>
                            <Card
                                sx={{
                                    border: request.status === 'PENDING' ? 2 : 1,
                                    borderColor: request.status === 'PENDING' ? 'warning.main' : 'divider'
                                }}
                            >
                                <CardContent>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item size={3} >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <Chip
                                                    icon={getStatusIcon(request.status)}
                                                    label={request.status}
                                                    color={getStatusColor(request.status)}
                                                    size="small"
                                                />
                                            </Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Request #{request.id.slice(-3)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDate(request.created_at)}
                                            </Typography>
                                        </Grid>

                                        <Grid item size={4}>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                Plan Upgrade
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body1">
                                                    {request.current_plan?.name || 'No Plan'}
                                                </Typography>
                                                <TrendingUp fontSize="small" color="primary" />
                                                <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                                    {request.requested_plan.name}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" color="text.secondary">
                                                {formatPrice(request.requested_plan)}
                                            </Typography>
                                        </Grid>

                                        <Grid item size={3}>
                                            {(request.status !== 'PROCESSED' && request.status !== 'ACTIVE' && request.status !== 'REJECTED') && (
                                                <Box>
                                                    <Typography variant="body2" color="warning.main">
                                                        Awaiting admin review
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Grid>

                                        <Grid item size={2}>
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <Tooltip title="View Details">
                                                    <IconButton
                                                        onClick={() => handleViewDetails(request)}
                                                        size="small"
                                                    >
                                                        <Visibility />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    {/* Show admin notes for processed requests */}
                                    {(request.status === 'ACTIVE' || request.status === 'REJECTED') && request.admin_notes && (
                                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                Admin Notes:
                                            </Typography>
                                            <Typography variant="body2">
                                                {request.admin_notes}
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Request Details Dialog */}
            <Dialog
                open={detailsDialogOpen}
                onClose={handleCloseDetails}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            Upgrade Request Details
                        </Typography>
                        <IconButton onClick={handleCloseDetails}>
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {selectedRequest && (
                        <Box>
                            {/* Request Status */}
                            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <Chip
                                        icon={getStatusIcon(selectedRequest.status)}
                                        label={selectedRequest.status}
                                        color={getStatusColor(selectedRequest.status)}
                                    />
                                    <Typography variant="h6">
                                        Request #{selectedRequest.id}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    Submitted on {formatDate(selectedRequest.created_at)}
                                </Typography>
                            </Paper>

                            {/* Plan Comparison */}
                            <Grid container spacing={3} sx={{ mb: 3 }}>
                                <Grid item size={6}>
                                    <Typography variant="h6" gutterBottom>Current Plan</Typography>
                                    <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                                        <Typography variant="h6" gutterBottom>
                                            {selectedRequest.current_plan?.name || 'No Active Plan'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            {formatPrice(selectedRequest.current_plan || {})}
                                        </Typography>
                                        <List dense>
                                            <ListItem sx={{ px: 0 }}>
                                                <ListItemText
                                                    primary={`${selectedRequest.current_plan.token_limit?.toLocaleString() || 'Unlimited'} tokens`}
                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                />
                                            </ListItem>
                                            <ListItem sx={{ px: 0 }}>
                                                <ListItemText
                                                    primary={`${selectedRequest.current_plan.knowledge_size_limit || 'Unlimited'}GB storage`}
                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                />
                                            </ListItem>
                                        </List>
                                    </Paper>
                                </Grid>
                                <Grid item size={6}>
                                    <Typography variant="h6" gutterBottom>
                                        <TrendingUp sx={{ verticalAlign: 'middle', mr: 1 }} />
                                        Requested Plan
                                    </Typography>
                                    <Paper sx={{ p: 2, border: '2px solid', borderColor: 'primary.main', bgcolor: 'primary.50' }}>
                                        <Typography variant="h6" gutterBottom color="primary.main">
                                            {selectedRequest.requested_plan.name}
                                        </Typography>
                                        <Typography variant="body2" color="primary.dark" gutterBottom>
                                            {formatPrice(selectedRequest.requested_plan)}
                                        </Typography>
                                        <List dense>
                                            <ListItem sx={{ px: 0 }}>
                                                <ListItemText
                                                    primary={`${selectedRequest.requested_plan.token_limit?.toLocaleString() || 'Unlimited'} tokens`}
                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                />
                                            </ListItem>
                                            <ListItem sx={{ px: 0 }}>
                                                <ListItemText
                                                    primary={`${selectedRequest.requested_plan.knowledge_size_limit || 'Unlimited'}GB storage`}
                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                />
                                            </ListItem>
                                            <ListItem sx={{ px: 0 }}>
                                                <ListItemText
                                                    primary={`${selectedRequest.requested_plan.validity_in_days} days validity`}
                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                />
                                            </ListItem>
                                        </List>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Admin Response */}
                            {(selectedRequest.status === 'APPROVED' || selectedRequest.status === 'REJECTED') && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>Admin Response</Typography>
                                    <Paper sx={{ p: 2, bgcolor: selectedRequest.status === 'APPROVED' ? 'success.50' : 'error.50' }}>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Processed by: {selectedRequest.processed_by}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Date: {formatDate(selectedRequest.processed_at)}
                                            </Typography>
                                        </Box>
                                        {selectedRequest.admin_notes && (
                                            <Typography variant="body2">
                                                {selectedRequest.admin_notes}
                                            </Typography>
                                        )}
                                    </Paper>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseDetails}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PlanUpgradeRequests;